/**
 * PollCard — inline poll renderer for the Chalk Talk feed.
 *
 * Loads the poll on mount, shows title + options as tappable buttons. After
 * voting the same card flips to result-bars view (live percentages).
 * Coaches always see voter names; parents see them only when the poll
 * creator enabled `show_voter_names_to_parents`.
 */
import { useEffect, useState } from "react";
import { CheckCircle2, Lock, Vote } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/chalkApi";

export default function PollCard({ pollId, isOwnPost = false }) {
  const [poll, setPoll] = useState(null);
  const [picks, setPicks] = useState(new Set());
  const [voting, setVoting] = useState(false);

  async function reload() {
    try {
      const d = await api.getPoll(pollId);
      setPoll(d.poll);
      setPicks(new Set(d.poll.my_picks || []));
    } catch (e) {
      toast.error(e.message || "Couldn't load poll.");
    }
  }

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [pollId]);

  if (!poll) {
    return <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 6 }}>Loading poll…</div>;
  }

  const multi = poll.poll_type === "multi";
  const hasVoted = (poll.my_picks || []).length > 0;
  const showResults = hasVoted || poll.closed;

  function toggle(id) {
    if (poll.closed) return;
    if (multi) {
      const next = new Set(picks);
      next.has(id) ? next.delete(id) : next.add(id);
      setPicks(next);
    } else {
      setPicks(new Set([id]));
    }
  }

  async function submit() {
    if (picks.size === 0) {
      toast.error("Pick at least one option.");
      return;
    }
    setVoting(true);
    try {
      await api.votePoll(pollId, Array.from(picks));
      await reload();
    } catch (e) {
      toast.error(e.message || "Couldn't submit your vote.");
    } finally {
      setVoting(false);
    }
  }

  const tint = isOwnPost ? "rgba(255,255,255,0.15)" : "rgba(46,125,82,0.08)";
  const fgMuted = isOwnPost ? "rgba(245,240,232,0.75)" : "var(--chalk-muted)";
  const fg = isOwnPost ? "#F5F0E8" : "var(--chalk-board)";

  return (
    <div
      data-testid={`poll-${pollId}`}
      style={{
        marginTop: 8, padding: "10px 12px",
        background: tint, borderRadius: 10,
      }}
    >
      <div style={{
        display: "flex", alignItems: "center", gap: 6,
        fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em",
        color: fgMuted, fontWeight: 700, marginBottom: 6,
      }}>
        {poll.closed
          ? <><Lock size={11} aria-hidden="true" /> Poll closed</>
          : <><Vote size={11} aria-hidden="true" /> Poll · {poll.total_voters} voted</>}
      </div>
      {poll.description && (
        <div style={{ fontSize: 12, color: fg, opacity: 0.8, marginBottom: 8, lineHeight: 1.4 }}>
          {poll.description}
        </div>
      )}
      {showResults ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {poll.options.map((o) => (
            <ResultBar key={o.id} option={o}
                       picked={picks.has(o.id)}
                       names={o.voter_names}
                       isOwnPost={isOwnPost} />
          ))}
        </div>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {poll.options.map((o) => {
              const sel = picks.has(o.id);
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => toggle(o.id)}
                  data-testid={`poll-option-${o.id}`}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "8px 12px", borderRadius: 8,
                    background: sel ? "var(--green-mid)" : (isOwnPost ? "rgba(255,255,255,0.08)" : "#fff"),
                    color: sel ? "#fff" : fg,
                    border: sel ? "1px solid var(--green-mid)"
                                : (isOwnPost ? "0.5px solid rgba(255,255,255,0.12)"
                                             : "0.5px solid var(--chalk-border)"),
                    fontSize: 13, fontWeight: 600,
                    textAlign: "left", cursor: "pointer",
                  }}
                >
                  {sel && <CheckCircle2 size={13} aria-hidden="true" />}
                  {o.label}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={submit}
            disabled={voting || picks.size === 0}
            data-testid={`poll-submit-${pollId}`}
            style={{
              marginTop: 10, padding: "7px 14px", borderRadius: 8,
              background: "var(--chalk-board)", color: "#fff", border: "none",
              fontFamily: "var(--font-body)", fontWeight: 700, fontSize: 11,
              letterSpacing: "0.08em", textTransform: "uppercase",
              cursor: voting ? "wait" : "pointer",
              opacity: voting || picks.size === 0 ? 0.55 : 1,
            }}
          >
            {voting ? "Submitting…" : "Submit vote"}
          </button>
        </>
      )}
    </div>
  );
}

function ResultBar({ option, picked, names, isOwnPost }) {
  const fg = isOwnPost ? "#F5F0E8" : "var(--chalk-board)";
  return (
    <div data-testid={`poll-result-${option.id}`}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: fg, marginBottom: 3 }}>
        <span style={{ fontWeight: picked ? 700 : 500 }}>
          {picked ? "✓ " : ""}{option.label}
        </span>
        <span>{option.percentage}% · {option.count}</span>
      </div>
      <div style={{
        height: 6, borderRadius: 3,
        background: isOwnPost ? "rgba(255,255,255,0.12)" : "rgba(46,125,82,0.10)",
        overflow: "hidden",
      }}>
        <div style={{
          width: `${option.percentage}%`, height: "100%",
          background: picked ? "var(--green-mid)" : (isOwnPost ? "rgba(245,240,232,0.5)" : "var(--chalk-board)"),
          transition: "width 0.4s ease",
        }} />
      </div>
      {names && names.length > 0 && (
        <div style={{ fontSize: 10, color: isOwnPost ? "rgba(245,240,232,0.6)" : "var(--chalk-muted)", marginTop: 3 }}>
          {names.slice(0, 3).join(", ")}{names.length > 3 ? ` +${names.length - 3} more` : ""}
        </div>
      )}
    </div>
  );
}
